import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Pencil } from 'lucide-react';
import { Avatar, Skeleton } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useAppAuth } from '@/lib/useAppAuth';
import { useAppUser } from '@/lib/useAppUser';
import { useKycStatus } from '@/lib/kyc/useKyc';
import { useKycIdentityPending } from '@/lib/kyc/useKycIdentityPending';
import { useOpenKycGate } from '@/store/kycGateStore';
import { useTradingPositionGetPortfolio, useTradingPositionListPositions, useUsersGetMe } from '@/lib/endpoints';
import { PlaeePositionResponseDtoStatus, TradingPositionListPositionsStatus } from '@/lib/models';
import verifyBadge from '@/assets/settings/verify-badge.svg';
import { InlineUsernameEditor } from './InlineUsernameEditor';

/** `+$4` / `-$1.50` — whole dollars unless cents matter. */
function fmtSignedDollars(value: number): string {
  const abs = Math.abs(value);
  const rounded = Math.round(abs * 100) / 100;
  const amount = Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(2);
  return `${value >= 0 ? '+' : '-'}$${amount}`;
}

/**
 * "N Active Markets" pill (verified users). Links to the portfolio; when there
 * are open positions it carries a green/red chip with the unrealized PnL.
 * Mounted only for verified users, which is what gates its queries.
 */
function ActiveMarketsPill() {
  const { data: positions = [] } = useTradingPositionListPositions({
    status: TradingPositionListPositionsStatus.open,
  });
  const { data: portfolio } = useTradingPositionGetPortfolio();

  const open = positions.filter((p) => p.status === PlaeePositionResponseDtoStatus.OPEN && p.quantity > 0);
  const marketCount = new Set(open.map((p) => p.marketId ?? p.positionId)).size;
  const pnl = portfolio?.totalUnrealizedPnl ?? 0;

  // Single line at every viewport: nowrap keeps the text from stacking,
  // smaller type below `sm` buys room next to the avatar, and when even
  // that can't fit the label truncates — the PnL chip and chevron never do.
  return (
    <Link
      to="/portfolio"
      data-testid="settings-active-markets"
      className="inline-flex max-w-full min-w-0 items-center gap-2 whitespace-nowrap rounded-pill bg-input-soft px-3 py-2 transition-opacity hover:opacity-90 sm:px-3.5"
    >
      {marketCount > 0 ? (
        <>
          <span className="min-w-0 truncate text-sm text-ink sm:text-base">
            <span className="font-medium">{marketCount}</span> Active Market{marketCount === 1 ? '' : 's'}
          </span>
          {pnl !== 0 && (
            <span
              className={cn(
                'shrink-0 rounded-pill px-2 py-0.5 text-xs font-semibold capitalize tracking-tight sm:px-2.5 sm:text-sm',
                pnl > 0 ? 'bg-up/10 text-up' : 'bg-down/10 text-down',
              )}
            >
              Value {fmtSignedDollars(pnl)}
            </span>
          )}
        </>
      ) : (
        <span className="truncate text-sm text-ink sm:text-base">No Active Markets</span>
      )}
      <ChevronRight aria-hidden="true" className="size-4 shrink-0 text-ink" />
    </Link>
  );
}

/**
 * Settings account header (Figma "Account verified / unverified"):
 * avatar with a KYC verify badge, the username with an in-place editor, and —
 * depending on identity-KYC state — the Active Markets pill (verified) or a
 * "Complete Verification" CTA that opens the shared {@link KycGate}.
 *
 * Data sources: `/users/me` owns the locally-editable `username` (+ fallback
 * chain per GLOSSARY: username → displayName → externalId); the avatar image
 * comes from the auth profile (`useAppUser`), which `/users/me` doesn't carry.
 */
export function AccountHeader() {
  const [editing, setEditing] = useState(false);
  const { isAuthenticated } = useAppAuth();
  const user = useAppUser();
  const { data: profile } = useUsersGetMe();
  const { data: kyc } = useKycStatus(isAuthenticated);
  const pendingIdentity = useKycIdentityPending();
  const openKycGate = useOpenKycGate();

  // `pendingIdentity` is null both when verified AND while /kyc/status is
  // still loading (fail-open) — gate the badge/CTA on the data having arrived.
  const kycLoaded = Boolean(kyc);
  const isVerified = kycLoaded && pendingIdentity === null;

  const displayName = profile?.username || profile?.displayName || profile?.externalId || '';

  return (
    <section aria-label="Account" className="flex items-center gap-3.5">
      <div className="relative shrink-0">
        <Avatar src={user?.profileImage} alt="" className="size-20 border-[3.5px] border-input-soft bg-surface" />
        {isVerified && <img src={verifyBadge} alt="Verified account" className="absolute -bottom-0.5 -right-1 size-7" />}
      </div>

      <div className="flex min-w-0 flex-1 flex-col items-start gap-1.5">
        <div className="flex w-full items-center gap-2 pl-3">
          {editing ? (
            <InlineUsernameEditor initialValue={profile?.username ?? ''} onDone={() => setEditing(false)} />
          ) : profile ? (
            <>
              <span className="truncate text-[22px] font-medium text-ink">{displayName}</span>
              <button
                type="button"
                onClick={() => setEditing(true)}
                aria-label="Edit username"
                className="shrink-0 p-1 text-purple transition-opacity hover:opacity-80"
              >
                <Pencil aria-hidden="true" className="size-3.5 fill-purple" />
              </button>
            </>
          ) : (
            <Skeleton className="h-7 w-40" />
          )}
        </div>

        {!kycLoaded ? (
          <Skeleton className="h-9 w-48 rounded-pill" />
        ) : isVerified ? (
          <ActiveMarketsPill />
        ) : (
          <button
            type="button"
            data-testid="settings-complete-verification"
            onClick={() => {
              if (pendingIdentity) void openKycGate(pendingIdentity.flowKey);
            }}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-pill border-[1.5px] border-verified bg-surface px-4 py-2',
              'text-base text-verified transition-opacity hover:opacity-85',
            )}
          >
            Complete Verification
            <ChevronRight aria-hidden="true" className="size-4" />
          </button>
        )}
      </div>
    </section>
  );
}
